import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  FaChartBar,
  FaCheckCircle,
  FaCopy,
  FaDownload,
  FaFileCsv,
  FaImage,
  FaMobileAlt,
  FaPaperPlane,
  FaPrint,
  FaSyncAlt,
  FaTelegramPlane,
  FaTimesCircle,
  FaTrashAlt,
  FaUserPlus,
  FaUsers,
} from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../Api";
import { isSuperAdmin } from "../auth";

const emptyUser = {
  username: "",
  password: "",
  role: "user",
  is_active: 1,
  permissions: [],
};

const emptyTelegramUser = {
  telegram_user_id: "",
  chat_id: "",
  username: "",
  first_name: "",
  last_name: "",
  phone_number: "",
  app_user_id: "",
  recipient_type: "receptionist",
  status: "allowed",
  is_master: 0,
  receive_scan_notifications: 1,
  notes: "",
};

const emptyTelegramSettings = {
  enabled: false,
  bot_name: "SCAN-APP",
  bot_token: "",
  bot_token_set: false,
  webhook_url: "",
  webhook_secret: "",
  master_phone: "9717321982",
  password_reset_enabled: true,
  admin_login_otp_enabled: true,
  scan_notifications_enabled: true,
  progress_step: 10,
};

const emptySkuMediaSettings = {
  flipkart_url_template: "https://www.flipkart.com/product/p/itme?pid={FSN}",
  myntra_url_template: "",
  r2_endpoint: "",
  r2_bucket: "",
  r2_public_url: "",
  r2_access_key_id: "",
  r2_secret_access_key: "",
  r2_secret_access_key_set: false,
};

const jsonText = (value, fallback) => {
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value ?? fallback, null, 2);
  } catch {
    return JSON.stringify(fallback, null, 2);
  }
};

const compactTableSx = {
  "& th": { fontWeight: 800, backgroundColor: "#f8fafc", whiteSpace: "nowrap" },
  "& td": { verticalAlign: "top" },
};

function SettingsSection({ panel, expanded, onChange, icon, title, subtitle, chips = [], children }) {
  return (
    <Accordion
      expanded={expanded === panel}
      onChange={onChange(panel)}
      disableGutters
      sx={{
        mb: 1.25,
        border: "1px solid #e5e7eb",
        borderRadius: "8px !important",
        boxShadow: "none",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: "100%", minWidth: 0 }}>
          <Box sx={{ color: "#2563eb", display: "flex", fontSize: 18 }}>{icon}</Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 900, lineHeight: 1.2 }}>{title}</Typography>
            <Typography variant="body2" color="text.secondary" noWrap>{subtitle}</Typography>
          </Box>
          <Stack direction="row" spacing={0.75} sx={{ display: { xs: "none", md: "flex" } }}>
            {chips.map(chip => (
              <Chip key={chip.label} label={chip.label} color={chip.color || "default"} size="small" variant={chip.variant || "outlined"} />
            ))}
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Divider sx={{ mb: 2 }} />
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

function statusChip(status) {
  const normalized = status || "pending";
  const color = normalized === "allowed" ? "success" : normalized === "refused" ? "error" : "warning";
  return <Chip size="small" color={color} label={normalized.toUpperCase()} />;
}

function telegramName(user) {
  const name = [user.first_name, user.last_name].filter(Boolean).join(" ").trim();
  return name || user.username || "-";
}

export default function SystemSettingsPage() {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState(emptyUser);
  const [permissionOptions, setPermissionOptions] = useState([]);
  const [telegramSettings, setTelegramSettings] = useState(emptyTelegramSettings);
  const [telegramUsers, setTelegramUsers] = useState([]);
  const [newTelegramUser, setNewTelegramUser] = useState(emptyTelegramUser);
  const [telegramBotLink, setTelegramBotLink] = useState({ bot_link: "", username: "" });
  const [showTelegramAdd, setShowTelegramAdd] = useState(false);
  const canManageUsers = isSuperAdmin();
  const [expandedSection, setExpandedSection] = useState("telegram");
  const [config, setConfig] = useState({
    print_slots: [],
    import_templates: [],
    graph_groups: [],
    sku_media: emptySkuMediaSettings,
  });

  const telegramCounts = useMemo(() => ({
    total: telegramUsers.length,
    pending: telegramUsers.filter(user => user.status === "pending").length,
    allowed: telegramUsers.filter(user => user.status === "allowed").length,
  }), [telegramUsers]);

  const fallbackBotLink = useMemo(() => {
    const username = String(telegramSettings.bot_name || "").replace(/^@/, "").trim();
    return /^[A-Za-z0-9_]{5,}$/.test(username) ? `https://t.me/${username}?start=register` : "";
  }, [telegramSettings.bot_name]);

  const inviteLink = telegramBotLink.bot_link || fallbackBotLink;
  const permissionOptionsForRole = (role) => {
    if (role === "admin") return permissionOptions;
    if (role === "user") return permissionOptions.filter(permission => ["cancel_packets", "return_claims"].includes(permission.key));
    return [];
  };

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const [
        configRes,
        telegramSettingsRes,
        telegramUsersRes,
        botLinkRes,
        usersRes,
        permissionOptionsRes,
      ] = await Promise.all([
        api.get("/api/admin/config"),
        api.get("/api/admin/telegram/settings"),
        api.get("/api/admin/telegram/users"),
        api.get("/api/admin/telegram/bot-link").catch(() => ({ data: { bot_link: "", username: "" } })),
        canManageUsers ? api.get("/api/admin/users") : Promise.resolve({ data: [] }),
        canManageUsers ? api.get("/api/admin/permission-options") : Promise.resolve({ data: [] }),
      ]);

      setConfig({
        print_slots: configRes.data.print_slots || [],
        import_templates: (configRes.data.import_templates || []).map(template => ({
          ...template,
          accepted_statuses: jsonText(template.accepted_statuses, []),
          header_map: jsonText(template.header_map, {}),
          date_formats: jsonText(template.date_formats, {}),
          default_quantity: template.default_quantity ?? "",
        })),
        graph_groups: configRes.data.graph_groups || [],
        sku_media: { ...emptySkuMediaSettings, ...(configRes.data.sku_media || {}), r2_secret_access_key: "" },
      });
      setUsers(usersRes.data || []);
      setPermissionOptions(permissionOptionsRes.data || []);
      setTelegramSettings({ ...emptyTelegramSettings, ...telegramSettingsRes.data, bot_token: "" });
      setTelegramUsers(telegramUsersRes.data || []);
      setTelegramBotLink(botLinkRes.data || { bot_link: "", username: "" });
    } catch (error) {
      console.error("Failed to load settings:", error);
      toast.error(error.response?.data?.error || "Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, [canManageUsers]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSectionChange = (panel) => (_event, isExpanded) => {
    setExpandedSection(isExpanded ? panel : false);
  };

  const updateConfigItem = (section, id, patch) => {
    setConfig(prev => ({
      ...prev,
      [section]: prev[section].map(item => item.id === id ? { ...item, ...patch } : item),
    }));
  };

  const updateSkuMediaSettings = (patch) => {
    setConfig(prev => ({
      ...prev,
      sku_media: { ...(prev.sku_media || emptySkuMediaSettings), ...patch },
    }));
  };

  const updateUserItem = (id, patch) => {
    setUsers(prev => prev.map(user => user.id === id ? { ...user, ...patch } : user));
  };

  const toggleUserPermission = (id, permissionKey) => {
    setUsers(prev => prev.map(user => {
      if (user.id !== id) return user;
      const permissions = Array.isArray(user.permissions) ? user.permissions : [];
      return {
        ...user,
        permissions: permissions.includes(permissionKey)
          ? permissions.filter(key => key !== permissionKey)
          : [...permissions, permissionKey],
      };
    }));
  };

  const toggleNewUserPermission = (permissionKey) => {
    setNewUser(prev => {
      const permissions = Array.isArray(prev.permissions) ? prev.permissions : [];
      return {
        ...prev,
        permissions: permissions.includes(permissionKey)
          ? permissions.filter(key => key !== permissionKey)
          : [...permissions, permissionKey],
      };
    });
  };

  const updateTelegramUserItem = (id, patch) => {
    setTelegramUsers(prev => prev.map(user => user.id === id ? { ...user, ...patch } : user));
  };

  const copyInviteLink = async () => {
    if (!inviteLink) {
      toast.error("Save a valid bot username or token first");
      return;
    }
    try {
      await navigator.clipboard.writeText(inviteLink);
      toast.success("Telegram invite link copied");
    } catch {
      toast.error("Could not copy invite link");
    }
  };

  const saveUser = async (user) => {
    try {
      await api.put(`/api/admin/users/${user.id}`, user);
      toast.success("User saved");
      await loadSettings();
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to save user");
    }
  };

  const addUser = async () => {
    if (!newUser.username.trim() || !newUser.password.trim()) {
      toast.error("Username and password are required");
      return;
    }
    try {
      await api.post("/api/admin/users", newUser);
      setNewUser(emptyUser);
      toast.success("User added");
      await loadSettings();
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to add user");
    }
  };

  const savePrintSlot = async (slot) => {
    try {
      await api.put(`/api/admin/print-slots/${slot.id}`, slot);
      toast.success("Print slot saved");
      await loadSettings();
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to save print slot");
    }
  };

  const saveImportTemplate = async (template) => {
    try {
      await api.put(`/api/admin/import-templates/${template.id}`, template);
      toast.success("Import template saved");
      await loadSettings();
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to save import template");
    }
  };

  const saveGraphGroup = async (group) => {
    try {
      await api.put(`/api/admin/graph-groups/${group.id}`, group);
      toast.success("Graph group saved");
      await loadSettings();
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to save graph group");
    }
  };

  const saveSkuMediaSettings = async () => {
    try {
      const payload = { ...(config.sku_media || emptySkuMediaSettings) };
      if (!payload.r2_secret_access_key) delete payload.r2_secret_access_key;
      const res = await api.put("/api/admin/sku-media-settings", payload);
      setConfig(prev => ({
        ...prev,
        sku_media: { ...emptySkuMediaSettings, ...res.data, r2_secret_access_key: "" },
      }));
      toast.success("SKU media settings saved");
      await loadSettings();
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to save SKU media settings");
    }
  };

  const saveTelegramSettings = async () => {
    try {
      const payload = { ...telegramSettings };
      if (!payload.bot_token) delete payload.bot_token;
      const res = await api.put("/api/admin/telegram/settings", payload);
      setTelegramSettings({ ...emptyTelegramSettings, ...res.data, bot_token: "" });
      toast.success("Telegram settings saved");
      await loadSettings();
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to save Telegram settings");
    }
  };

  const setTelegramWebhook = async () => {
    try {
      const res = await api.post("/api/admin/telegram/webhook", {
        webhook_url: telegramSettings.webhook_url,
      });
      setTelegramSettings(prev => ({ ...prev, webhook_url: res.data.webhook_url }));
      toast.success("Telegram webhook saved");
      await loadSettings();
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to set Telegram webhook");
    }
  };

  const addTelegramUser = async () => {
    if (!newTelegramUser.chat_id) {
      toast.error("Chat ID is required");
      return;
    }
    try {
      await api.post("/api/admin/telegram/users", newTelegramUser);
      setNewTelegramUser(emptyTelegramUser);
      setShowTelegramAdd(false);
      toast.success("Telegram recipient added");
      await loadSettings();
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to add Telegram user");
    }
  };

  const saveTelegramUser = async (user) => {
    try {
      await api.put(`/api/admin/telegram/users/${user.id}`, user);
      toast.success("Telegram recipient saved");
      await loadSettings();
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to save Telegram user");
    }
  };

  const deleteTelegramUser = async (user) => {
    if (!window.confirm(`Remove Telegram recipient "${telegramName(user)}"?`)) return;
    try {
      await api.delete(`/api/admin/telegram/users/${user.id}`);
      toast.success("Telegram recipient removed");
      await loadSettings();
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to remove Telegram user");
    }
  };

  const sendTelegramTest = async (chatId = "") => {
    try {
      await api.post("/api/admin/telegram/test-message", {
        chat_id: chatId,
        text: "SCAN-APP Telegram test message",
      });
      toast.success("Telegram test sent");
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to send Telegram test");
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, bgcolor: "#f8fafc", minHeight: "calc(100vh - 80px)" }}>
      <Stack direction={{ xs: "column", md: "row" }} alignItems={{ xs: "stretch", md: "center" }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: 0 }}>Settings</Typography>
          <Typography color="text.secondary">Open only the section you need, update, then collapse it again.</Typography>
        </Box>
        <Button variant="outlined" startIcon={<FaSyncAlt />} onClick={loadSettings} disabled={loading} sx={{ alignSelf: { xs: "flex-start", md: "center" } }}>
          Refresh
        </Button>
      </Stack>

      <SettingsSection
        panel="androidapp"
        expanded={expandedSection}
        onChange={handleSectionChange}
        icon={<FaMobileAlt />}
        title="Android App"
        subtitle="Download the SCAN scanner app (.apk) for your phone"
        chips={[{ label: "APK", color: "success" }]}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Install the SCAN scanner app on an Android phone. Open this on the phone (or scan/share
          the link), download the .apk, then tap it to install. Android may warn about installing
          from outside the Play Store — allow it for your browser to finish.
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
          <Button
            variant="contained"
            color="success"
            startIcon={<FaDownload />}
            href="/downloads/scan-app.apk?v=1.8"
            download
          >
            Download APK
          </Button>
          <Button
            variant="outlined"
            startIcon={<FaCopy />}
            onClick={() => {
              const url = `${window.location.origin}/downloads/scan-app.apk?v=1.8`;
              navigator.clipboard?.writeText(url);
              toast.success("Download link copied");
            }}
          >
            Copy link
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all" }}>
            {`${(typeof window !== "undefined" && window.location.origin) || "https://scan.slowloris.in"}/downloads/scan-app.apk?v=1.8`}
          </Typography>
        </Stack>
      </SettingsSection>

      {canManageUsers && (
        <SettingsSection
          panel="users"
          expanded={expandedSection}
          onChange={handleSectionChange}
          icon={<FaUsers />}
          title="Login Users"
          subtitle="Create scanner/admin logins and assign modules"
          chips={[{ label: `${users.length} users` }]}
        >
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
            <TextField size="small" label="Username" value={newUser.username} onChange={(e) => setNewUser({ ...newUser, username: e.target.value })} />
            <TextField size="small" label="Password" type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Role</InputLabel>
              <Select label="Role" value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value, permissions: [] })}>
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="super_admin">Super Admin</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel control={<Checkbox checked={!!newUser.is_active} onChange={(e) => setNewUser({ ...newUser, is_active: e.target.checked ? 1 : 0 })} />} label="Active" />
            <Button variant="contained" onClick={addUser} startIcon={<FaUserPlus />}>Add User</Button>
          </Stack>
          {permissionOptionsForRole(newUser.role).length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              {permissionOptionsForRole(newUser.role).map(permission => (
                <FormControlLabel
                  key={permission.key}
                  control={<Checkbox checked={(newUser.permissions || []).includes(permission.key)} onChange={() => toggleNewUserPermission(permission.key)} />}
                  label={permission.label}
                />
              ))}
            </Stack>
          )}
          <TableContainer sx={{ maxHeight: 340, border: "1px solid #e5e7eb", borderRadius: 1 }}>
            <Table size="small" stickyHeader sx={compactTableSx}>
              <TableHead>
                <TableRow>
                  <TableCell>Username</TableCell>
                  <TableCell>New Password</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Allowed Modules</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell>Save</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map(user => (
                  <TableRow key={user.id} hover>
                    <TableCell><TextField size="small" value={user.username} onChange={(e) => updateUserItem(user.id, { username: e.target.value })} /></TableCell>
                    <TableCell><TextField size="small" type="password" placeholder="Leave blank" value={user.password || ""} onChange={(e) => updateUserItem(user.id, { password: e.target.value })} /></TableCell>
                    <TableCell>
                      <Select size="small" value={user.role} onChange={(e) => updateUserItem(user.id, { role: e.target.value, permissions: [] })}>
                        <MenuItem value="user">User</MenuItem>
                        <MenuItem value="admin">Admin</MenuItem>
                        <MenuItem value="super_admin">Super Admin</MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell sx={{ minWidth: 320 }}>
                      {permissionOptionsForRole(user.role).length > 0 ? (
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {permissionOptionsForRole(user.role).map(permission => (
                            <FormControlLabel
                              key={permission.key}
                              control={<Checkbox checked={(user.permissions || []).includes(permission.key)} onChange={() => toggleUserPermission(user.id, permission.key)} />}
                              label={permission.label}
                            />
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {user.role === "super_admin" ? "Full access" : "Scanner login only"}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell><Checkbox checked={!!user.is_active} onChange={(e) => updateUserItem(user.id, { is_active: e.target.checked ? 1 : 0 })} /></TableCell>
                    <TableCell><Button size="small" variant="contained" onClick={() => saveUser(user)}>Save</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </SettingsSection>
      )}

      <SettingsSection
        panel="telegram"
        expanded={expandedSection}
        onChange={handleSectionChange}
        icon={<FaTelegramPlane />}
        title="Telegram"
        subtitle="Invite staff, approve recipients, and control scan alerts"
        chips={[
          { label: `${telegramCounts.pending} pending`, color: telegramCounts.pending ? "warning" : "default" },
          { label: `${telegramCounts.allowed} allowed`, color: telegramCounts.allowed ? "success" : "default" },
        ]}
      >
        <Stack spacing={2}>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "stretch" }}>
            <Box sx={{ flex: 1, minWidth: 280, bgcolor: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 1, p: 1.5 }}>
              <Typography variant="caption" color="text.secondary">Share this link with staff to register</Typography>
              <Typography sx={{ fontWeight: 900, color: "#2563eb", wordBreak: "break-all", mt: 0.25 }}>
                {inviteLink || "Save Telegram bot token, then refresh to generate the invite link."}
              </Typography>
            </Box>
            <Button variant="outlined" startIcon={<FaCopy />} onClick={copyInviteLink} disabled={!inviteLink}>
              Copy Link
            </Button>
            <Button variant="contained" startIcon={<FaUserPlus />} onClick={() => setShowTelegramAdd(prev => !prev)}>
              {showTelegramAdd ? "Close Add" : "Add Manually"}
            </Button>
          </Box>

          {!telegramSettings.enabled && (
            <Alert severity="warning">Telegram is currently disabled. Enable it and save settings before expecting alerts or OTP messages.</Alert>
          )}

          <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 1, p: 1.5, bgcolor: "#fff" }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <FormControlLabel
                control={<Checkbox checked={!!telegramSettings.enabled} onChange={(e) => setTelegramSettings({ ...telegramSettings, enabled: e.target.checked })} />}
                label="Enabled"
              />
              <TextField size="small" label="Bot Username / Name" value={telegramSettings.bot_name} onChange={(e) => setTelegramSettings({ ...telegramSettings, bot_name: e.target.value })} />
              <TextField
                size="small"
                label={telegramSettings.bot_token_set ? "Bot Token (set)" : "Bot Token"}
                type="password"
                value={telegramSettings.bot_token}
                onChange={(e) => setTelegramSettings({ ...telegramSettings, bot_token: e.target.value })}
                placeholder={telegramSettings.bot_token_set ? "Leave blank to keep" : ""}
                sx={{ minWidth: 260 }}
              />
              <TextField size="small" label="Master Phone" value={telegramSettings.master_phone} onChange={(e) => setTelegramSettings({ ...telegramSettings, master_phone: e.target.value })} />
              <TextField size="small" label="Webhook URL" value={telegramSettings.webhook_url} onChange={(e) => setTelegramSettings({ ...telegramSettings, webhook_url: e.target.value })} sx={{ minWidth: 320 }} />
              <TextField size="small" label="Webhook Secret" value={telegramSettings.webhook_secret} onChange={(e) => setTelegramSettings({ ...telegramSettings, webhook_secret: e.target.value })} sx={{ minWidth: 220 }} />
              <TextField size="small" label="Scan Summary Every %" type="number" value={telegramSettings.progress_step} onChange={(e) => setTelegramSettings({ ...telegramSettings, progress_step: e.target.value })} sx={{ width: 180 }} />
              <FormControlLabel
                control={<Checkbox checked={!!telegramSettings.password_reset_enabled} onChange={(e) => setTelegramSettings({ ...telegramSettings, password_reset_enabled: e.target.checked })} />}
                label="OTP Reset"
              />
              <FormControlLabel
                control={<Checkbox checked={!!telegramSettings.admin_login_otp_enabled} onChange={(e) => setTelegramSettings({ ...telegramSettings, admin_login_otp_enabled: e.target.checked })} />}
                label="Admin Login OTP"
              />
              <FormControlLabel
                control={<Checkbox checked={!!telegramSettings.scan_notifications_enabled} onChange={(e) => setTelegramSettings({ ...telegramSettings, scan_notifications_enabled: e.target.checked })} />}
                label="Scan Alerts"
              />
              <Button variant="contained" onClick={saveTelegramSettings}>Save</Button>
              <Button variant="outlined" onClick={setTelegramWebhook}>Set Webhook</Button>
              <Button variant="outlined" startIcon={<FaPaperPlane />} onClick={() => sendTelegramTest()}>Test All</Button>
            </Stack>
          </Box>

          {showTelegramAdd && (
            <Box sx={{ border: "1px solid #dbeafe", borderRadius: 1, p: 1.5, bgcolor: "#f8fbff" }}>
              <Typography sx={{ fontWeight: 900, mb: 1 }}>Add Telegram Recipient Manually</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <TextField size="small" label="Name" value={newTelegramUser.first_name} onChange={(e) => setNewTelegramUser({ ...newTelegramUser, first_name: e.target.value })} />
                <TextField size="small" label="Phone" value={newTelegramUser.phone_number} onChange={(e) => setNewTelegramUser({ ...newTelegramUser, phone_number: e.target.value })} />
                <TextField size="small" label="Chat ID" value={newTelegramUser.chat_id} onChange={(e) => setNewTelegramUser({ ...newTelegramUser, chat_id: e.target.value })} />
                <TextField size="small" label="Telegram User ID" value={newTelegramUser.telegram_user_id} onChange={(e) => setNewTelegramUser({ ...newTelegramUser, telegram_user_id: e.target.value })} />
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>App User</InputLabel>
                  <Select label="App User" value={newTelegramUser.app_user_id} onChange={(e) => setNewTelegramUser({ ...newTelegramUser, app_user_id: e.target.value })}>
                    <MenuItem value="">None</MenuItem>
                    {users.map(user => <MenuItem key={user.id} value={user.id}>{user.username}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 145 }}>
                  <InputLabel>Type</InputLabel>
                  <Select label="Type" value={newTelegramUser.recipient_type} onChange={(e) => setNewTelegramUser({ ...newTelegramUser, recipient_type: e.target.value })}>
                    <MenuItem value="receptionist">Receptionist</MenuItem>
                    <MenuItem value="manager">Manager</MenuItem>
                    <MenuItem value="owner">Owner</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel>Status</InputLabel>
                  <Select label="Status" value={newTelegramUser.status} onChange={(e) => setNewTelegramUser({ ...newTelegramUser, status: e.target.value })}>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="allowed">Allowed</MenuItem>
                    <MenuItem value="refused">Refused</MenuItem>
                  </Select>
                </FormControl>
                <FormControlLabel control={<Checkbox checked={!!newTelegramUser.is_master} onChange={(e) => setNewTelegramUser({ ...newTelegramUser, is_master: e.target.checked ? 1 : 0 })} />} label="Master" />
                <FormControlLabel control={<Checkbox checked={!!newTelegramUser.receive_scan_notifications} onChange={(e) => setNewTelegramUser({ ...newTelegramUser, receive_scan_notifications: e.target.checked ? 1 : 0 })} />} label="Alerts" />
                <Button variant="contained" onClick={addTelegramUser}>Save Recipient</Button>
              </Stack>
            </Box>
          )}

          <TableContainer sx={{ maxHeight: 380, border: "1px solid #e5e7eb", borderRadius: 1 }}>
            <Table size="small" stickyHeader sx={compactTableSx}>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Chat ID</TableCell>
                  <TableCell>App User</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Alerts</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {telegramUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: "text.secondary" }}>
                      No recipients yet. Share the invite link or add one manually.
                    </TableCell>
                  </TableRow>
                ) : telegramUsers.map(user => (
                  <TableRow key={user.id} hover>
                    <TableCell sx={{ minWidth: 190 }}>
                      <Stack spacing={0.5}>
                        <TextField size="small" label="Name" value={user.first_name || ""} onChange={(e) => updateTelegramUserItem(user.id, { first_name: e.target.value })} />
                        <TextField size="small" label="Username" value={user.username || ""} onChange={(e) => updateTelegramUserItem(user.id, { username: e.target.value })} />
                      </Stack>
                    </TableCell>
                    <TableCell><TextField size="small" value={user.phone_number || ""} onChange={(e) => updateTelegramUserItem(user.id, { phone_number: e.target.value })} sx={{ width: 130 }} /></TableCell>
                    <TableCell><TextField size="small" value={user.chat_id || ""} onChange={(e) => updateTelegramUserItem(user.id, { chat_id: e.target.value })} sx={{ width: 140 }} /></TableCell>
                    <TableCell>
                      <Select size="small" value={user.app_user_id || ""} onChange={(e) => updateTelegramUserItem(user.id, { app_user_id: e.target.value })} sx={{ minWidth: 130 }}>
                        <MenuItem value="">None</MenuItem>
                        {users.map(appUser => <MenuItem key={appUser.id} value={appUser.id}>{appUser.username}</MenuItem>)}
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select size="small" value={user.recipient_type || "receptionist"} onChange={(e) => updateTelegramUserItem(user.id, { recipient_type: e.target.value })} sx={{ minWidth: 130 }}>
                        <MenuItem value="receptionist">Receptionist</MenuItem>
                        <MenuItem value="manager">Manager</MenuItem>
                        <MenuItem value="owner">Owner</MenuItem>
                        <MenuItem value="other">Other</MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell>{statusChip(user.status)}</TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <FormControlLabel control={<Checkbox checked={!!user.is_master} onChange={(e) => updateTelegramUserItem(user.id, { is_master: e.target.checked ? 1 : 0 })} />} label="Master" />
                        <FormControlLabel control={<Checkbox checked={!!user.receive_scan_notifications} onChange={(e) => updateTelegramUserItem(user.id, { receive_scan_notifications: e.target.checked ? 1 : 0 })} />} label="Alerts" />
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.75} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                        <Button size="small" variant="contained" onClick={() => saveTelegramUser(user)}>Save</Button>
                        {user.status !== "allowed" && (
                          <Tooltip title="Allow">
                            <IconButton size="small" color="success" onClick={() => saveTelegramUser({ ...user, status: "allowed" })}>
                              <FaCheckCircle />
                            </IconButton>
                          </Tooltip>
                        )}
                        {user.status !== "refused" && (
                          <Tooltip title="Refuse">
                            <IconButton size="small" color="error" onClick={() => saveTelegramUser({ ...user, status: "refused" })}>
                              <FaTimesCircle />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Test">
                          <IconButton size="small" onClick={() => sendTelegramTest(user.chat_id)}>
                            <FaPaperPlane />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => deleteTelegramUser(user)}>
                            <FaTrashAlt />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </SettingsSection>

      <SettingsSection
        panel="print-slots"
        expanded={expandedSection}
        onChange={handleSectionChange}
        icon={<FaPrint />}
        title="Print Slots"
        subtitle="Rename and order print quantity fields"
        chips={[{ label: `${config.print_slots.length} slots` }]}
      >
        <TableContainer sx={{ border: "1px solid #e5e7eb", borderRadius: 1 }}>
          <Table size="small" sx={compactTableSx}>
            <TableHead>
              <TableRow>
                <TableCell>Field</TableCell>
                <TableCell>Label</TableCell>
                <TableCell>Order</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Save</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {config.print_slots.map(slot => (
                <TableRow key={slot.id} hover>
                  <TableCell sx={{ fontWeight: "bold" }}>{slot.field_name}</TableCell>
                  <TableCell><TextField size="small" value={slot.label} onChange={(e) => updateConfigItem("print_slots", slot.id, { label: e.target.value })} /></TableCell>
                  <TableCell><TextField size="small" type="number" value={slot.sort_order} onChange={(e) => updateConfigItem("print_slots", slot.id, { sort_order: e.target.value })} /></TableCell>
                  <TableCell><Checkbox checked={!!slot.is_active} onChange={(e) => updateConfigItem("print_slots", slot.id, { is_active: e.target.checked ? 1 : 0 })} /></TableCell>
                  <TableCell><Button size="small" variant="contained" onClick={() => savePrintSlot(slot)}>Save</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </SettingsSection>

      <SettingsSection
        panel="sku-media"
        expanded={expandedSection}
        onChange={handleSectionChange}
        icon={<FaImage />}
        title="SKU Image Media"
        subtitle="Configure Flipkart/Myntra product URLs and Cloudflare R2 storage"
        chips={[
          { label: config.sku_media?.r2_secret_access_key_set ? "R2 secret set" : "R2 secret missing", color: config.sku_media?.r2_secret_access_key_set ? "success" : "warning" },
        ]}
      >
        <Stack spacing={1.5}>
          <Alert severity="info">
            Use <strong>{"{FSN}"}</strong> in the URL template. Flipkart default: https://www.flipkart.com/product/p/itme?pid={"{FSN}"}.
          </Alert>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
            <TextField
              label="Flipkart URL Template"
              value={config.sku_media?.flipkart_url_template || ""}
              onChange={(e) => updateSkuMediaSettings({ flipkart_url_template: e.target.value })}
              fullWidth
            />
            <TextField
              label="Myntra URL Template"
              value={config.sku_media?.myntra_url_template || ""}
              onChange={(e) => updateSkuMediaSettings({ myntra_url_template: e.target.value })}
              fullWidth
            />
          </Stack>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
            <TextField
              label="R2 Endpoint"
              value={config.sku_media?.r2_endpoint || ""}
              onChange={(e) => updateSkuMediaSettings({ r2_endpoint: e.target.value })}
              fullWidth
            />
            <TextField
              label="R2 Bucket"
              value={config.sku_media?.r2_bucket || ""}
              onChange={(e) => updateSkuMediaSettings({ r2_bucket: e.target.value })}
              sx={{ minWidth: 180 }}
            />
            <TextField
              label="R2 Public URL"
              value={config.sku_media?.r2_public_url || ""}
              onChange={(e) => updateSkuMediaSettings({ r2_public_url: e.target.value })}
              fullWidth
            />
          </Stack>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ xs: "stretch", md: "center" }}>
            <TextField
              label="R2 Access Key ID"
              value={config.sku_media?.r2_access_key_id || ""}
              onChange={(e) => updateSkuMediaSettings({ r2_access_key_id: e.target.value })}
              fullWidth
            />
            <TextField
              label={config.sku_media?.r2_secret_access_key_set ? "New R2 Secret Access Key" : "R2 Secret Access Key"}
              type="password"
              value={config.sku_media?.r2_secret_access_key || ""}
              onChange={(e) => updateSkuMediaSettings({ r2_secret_access_key: e.target.value })}
              helperText={config.sku_media?.r2_secret_access_key_set ? "Leave blank to keep existing secret." : ""}
              fullWidth
            />
            <Button variant="contained" onClick={saveSkuMediaSettings} sx={{ minWidth: 120 }}>
              Save
            </Button>
          </Stack>
        </Stack>
      </SettingsSection>

      <SettingsSection
        panel="import-templates"
        expanded={expandedSection}
        onChange={handleSectionChange}
        icon={<FaFileCsv />}
        title="CSV Import Templates"
        subtitle="CSV mappings stay tucked away until needed"
        chips={[{ label: `${config.import_templates.length} templates` }]}
      >
        <Stack spacing={1.25}>
          {config.import_templates.map(template => (
            <Paper key={template.id} variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                <TextField size="small" label="Name" value={template.label} onChange={(e) => updateConfigItem("import_templates", template.id, { label: e.target.value })} />
                <TextField size="small" label="Match Header" value={template.match_header || ""} onChange={(e) => updateConfigItem("import_templates", template.id, { match_header: e.target.value })} />
                <TextField size="small" label="Default Qty" type="number" value={template.default_quantity} onChange={(e) => updateConfigItem("import_templates", template.id, { default_quantity: e.target.value })} sx={{ width: 120 }} />
                <TextField size="small" label="Order" type="number" value={template.sort_order} onChange={(e) => updateConfigItem("import_templates", template.id, { sort_order: e.target.value })} sx={{ width: 110 }} />
                <FormControlLabel control={<Checkbox checked={!!template.is_default} onChange={(e) => updateConfigItem("import_templates", template.id, { is_default: e.target.checked ? 1 : 0 })} />} label="Default" />
                <FormControlLabel control={<Checkbox checked={!!template.is_active} onChange={(e) => updateConfigItem("import_templates", template.id, { is_active: e.target.checked ? 1 : 0 })} />} label="Active" />
                <Button variant="contained" onClick={() => saveImportTemplate(template)}>Save</Button>
              </Stack>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                <TextField label="Accepted Statuses JSON" value={template.accepted_statuses} onChange={(e) => updateConfigItem("import_templates", template.id, { accepted_statuses: e.target.value })} multiline minRows={4} fullWidth />
                <TextField label="Header Map JSON" value={template.header_map} onChange={(e) => updateConfigItem("import_templates", template.id, { header_map: e.target.value })} multiline minRows={4} fullWidth />
                <TextField label="Date Formats JSON" value={template.date_formats} onChange={(e) => updateConfigItem("import_templates", template.id, { date_formats: e.target.value })} multiline minRows={4} fullWidth />
              </Stack>
            </Paper>
          ))}
        </Stack>
      </SettingsSection>

      <SettingsSection
        panel="graph-groups"
        expanded={expandedSection}
        onChange={handleSectionChange}
        icon={<FaChartBar />}
        title="Graph Groups"
        subtitle="Control dashboard graph grouping labels"
        chips={[{ label: `${config.graph_groups.length} groups` }]}
      >
        <TableContainer sx={{ border: "1px solid #e5e7eb", borderRadius: 1 }}>
          <Table size="small" sx={compactTableSx}>
            <TableHead>
              <TableRow>
                <TableCell>Value</TableCell>
                <TableCell>Label</TableCell>
                <TableCell>Order</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Save</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {config.graph_groups.map(group => (
                <TableRow key={group.id} hover>
                  <TableCell sx={{ fontWeight: "bold" }}>{group.value}</TableCell>
                  <TableCell><TextField size="small" value={group.label} onChange={(e) => updateConfigItem("graph_groups", group.id, { label: e.target.value })} /></TableCell>
                  <TableCell><TextField size="small" type="number" value={group.sort_order} onChange={(e) => updateConfigItem("graph_groups", group.id, { sort_order: e.target.value })} /></TableCell>
                  <TableCell><Checkbox checked={!!group.is_active} onChange={(e) => updateConfigItem("graph_groups", group.id, { is_active: e.target.checked ? 1 : 0 })} /></TableCell>
                  <TableCell><Button size="small" variant="contained" onClick={() => saveGraphGroup(group)}>Save</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </SettingsSection>
    </Box>
  );
}
